import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { toast } from 'react-toastify'

import { baseURL, clearMerchantTokens, listMerchantTokens } from 'api/axios'
import { DataService } from 'api/DataService'
import axios from 'axios'
import { hydratePrefs } from 'features/table-columns'
import jwtDecode from 'jwt-decode'
import {
  aggregatedScope,
  clearStoredSession,
  destroyGroupCookies,
  getGroupToken,
  getLoginUuid,
  GroupSession,
  isPathAllowedInGroup,
  persistGroupSession,
  persistLoginUuid,
  readGroupSession,
  readStoredAccounts,
  saveGroupMerchants,
  saveStoredAccounts,
  SessionAccount,
  SessionGroupOption,
  SessionGroupPayload
} from 'lib/merchant-group'
import mixpanel from 'mixpanel-browser'
import { useSession } from 'next-auth/react'
import Router from 'next/router'
import { parseCookies, setCookie } from 'nookies'
import { getRedirectUrlByRole, REDIRECT_URL_BY_ROLE } from 'pages'
import { ExtendedSession } from 'pages/api/auth/[...nextauth]'

import { handleGoogleSignOut } from 'components/RegisterForm/components/GoogleAuth/utils/logoutGoogleAuth'

import decodeBase64 from 'utils/decode-base64-to-string'
import { destroySession } from 'utils/DestroySession'
import { sessionDocument } from 'utils/document'
import encodeBase64 from 'utils/encode-to-base64'
import {
  CAPABILITIES_SYNC_EVENT,
  CapabilitiesSyncDetail,
  CREATION_BLOCKED_SYNC_EVENT,
  CreationBlockedSyncDetail
} from 'utils/plan-upgrade-event'
import { SESSION_COOKIE_OPTIONS } from 'utils/sessionCookie'

type Props = {
  children: React.ReactNode
}

export type Plan =
  | 'free'
  | 'basic'
  | 'pro'
  | 'ultra'
  | 'plan_trial'
  | 'start'
  | 'grow'
  | 'scale'
  | 'basic_mei'
  | 'basic_cora'
  | 'custom'
  | 'legacy'

export type UserStatus =
  | 'demo'
  | 'rejected'
  | 'suspended'
  | 'blocked'
  | 'trial'
  | 'enabled'
  | 'checking'

export type UserRole =
  | 'admin'
  | 'operator'
  | 'financial_operator'
  | 'billing_operator'
  | 'user'
  | 'guest'

export type User = {
  uuid: string
  name: string
  logo?: string
  automatic_transfer: boolean | string
  pro: boolean
  nfse: boolean
  role: UserRole
  status: UserStatus
  email: string
  merchant_email?: string
  stage: string
  document: string
  phone?: string
  installment_billings: boolean
  merchant_user_name: string
  portal_personalized: boolean
  subdomain?: string
  current_plan: string
  // Trial (SC-10668): vêm do to_json_auth para que qualquer tela possa exibir o
  // prazo sem uma chamada extra. null/undefined = merchant fora do trial.
  trial_days_left?: number | null
  trial_ends_at?: string | null
  cora_bank?: boolean
  cora_enabled?: boolean
  custom_invoice_domain?: string
  custom_portal_domain?: string
  // Canal de aquisição (SC-10813). O UTM só existe na URL do cadastro, então
  // sem isto nenhum evento depois do login carrega origem — 92% chegavam sem
  // canal e a análise por mídia só era possível via query no banco.
  acquisition_source?: string
  acquisition_medium?: string
  acquisition_campaign?: string
}

type SignInParams = {
  email: string
  password?: string
  id_token?: string
  phone?: string
  login_type?: 'email' | 'google'
  cf_turnstile_token?: string | null
}

type SignInResponse = {
  data: { records: object }
  error: string
  message: string
  status: number
}

type ModalPricingType = {
  isOpen: boolean
  plan: Plan
  resourceName: string
  // Qual oferta o usuário viu, para o clique poder ser atribuído ao teste A/B
  // do paywall. Opcional: nem toda porta de entrada do modal nasce de um
  // bloqueio de plano.
  variant?: string
}

type Capability = {
  has_access: boolean
  plan: Plan
}

type CapabilityCreditCards = {
  d30_access: boolean
  d30_antecipated_access: boolean
  d15_access: boolean
  d7_access: boolean
  d1_access: boolean
  allow_antecipation: boolean
  /**
   * Plano de recebimento VIGENTE (SC-10370). As chaves acima são permissão ("pode usar"),
   * estas são estado ("está usando") — um merchant em D+30 STANDARD com d7_access ligado
   * é normal. Vem daqui e não do GET /v3/credit_card_plans porque aquele endpoint só
   * responde a quem passa nas 5 travas de elegibilidade.
   */
  current_plan_name: string
  current_plan_antecipated: boolean
  current_plan_antecipated_days: number
  /** Null quando a capability não tem plano de cartão configurado. */
  current_plan_settlement_text: string | null
}

type CapabilityExtraNotificationFees = {
  email_fee: string
  sms_fee: string
  wpp_fee: string
}
type CapabilityOthers = {
  tracking_scripts: boolean
  webhooks: boolean
}
type Capabilities = {
  'contract.template': Capability
  'contract.batch': Capability
  'billing_ruler.smart': Capability
  'billing_ruler.agressive': Capability
  'billing_ruler.customized': Capability
  'announcement.documents': Capability
  credit_card_plans: CapabilityCreditCards
  'product_link.personalized_output_url': Capability
  'product_link.personalized_url': Capability
  accounts_payable: Capability
  // Módulos financeiros avançados (SC-9544): sem acesso a tela abre com prévia
  // mockada e CTA de upgrade, em vez de sair do menu.
  dre: Capability
  cash_flow: Capability
  accounts_receivable: Capability
  bank_reconciliation: Capability
  'billing.bonus': Capability
  'custom_field.field_types.short_text': Capability
  'custom_field.field_types.radio': Capability
  'custom_field.field_types.checkbox': Capability
  'custom_field.field_types.long_text': Capability
  'custom_field.field_types.accept': Capability
  'custom_field.field_types.upload': Capability
  'custom_field.field_types.date': Capability
  extra_notification: CapabilityExtraNotificationFees
  'custom_field.contexts.internal_customer': Capability
  'custom_field.contexts.customer': Capability
  'custom_field.contexts.product_link': Capability
  'custom_field.contexts.contract_model': Capability
  // SC-11871: max_billings = limite de cobranças por carnê da empresa (padrão 60).
  carne: Capability & { max_billings?: number }
  others: CapabilityOthers
  // SC-10696: libera o card do assistente de IA (MCP) em Configurações.
  mcp: Capability
  workflow: Capability
  coupons: Capability
  whatsapp_coexistence: {
    enabled: boolean
    status: string
  }
  conta_azul: Capability
  // SC-10274: envio dos e-mails pelo servidor SMTP do próprio merchant.
  custom_smtp: Capability
}

// SC-10999: destino da troca de conta. Empresa ou visão multi-empresas.
export type SwitchAccountTarget =
  | { merchantUuid: string; merchantGroupUuid?: never }
  | { merchantGroupUuid: string; merchantUuid?: never }

export type SwitchAccountOptions = {
  // Tela aberta depois da troca. Sem ela, fica na tela atual (sem os uuids e
  // o filtro de empresa, que eram da conta anterior).
  redirectTo?: string
}

// Chaves do localStorage que guardam dados da empresa anterior e vazariam para
// a próxima numa troca de conta (o `cb:first-login:<uuid>` fica: é por
// empresa, e apagá-lo reenviaria o `first_login` de quem volta para ela).
const MERCHANT_SCOPED_STORAGE_KEYS = [
  'chat-assistant-history',
  'banner',
  'products_view_mode'
]

export const initialAuthProviderValues = {
  user: null,
  isAuthenticated: false,
  setUser: () => null,
  signIn: () => null,
  signInWithToken: () => null,
  signOut: () => null
}

export type AuthContextType = {
  isAuthenticated: boolean
  user: User | null
  capabilities: Capabilities | null
  showUpgradeModal: boolean
  showNewPlansModal: boolean
  creationBlocked: boolean
  setUser: React.Dispatch<React.SetStateAction<User | null>>
  signIn: (data: SignInParams) => Promise<SignInResponse>
  signInWithToken: (data: { token: string }) => void
  signOut: () => void
  stepCreating: string
  setStepCreating: React.Dispatch<React.SetStateAction<string>>
  modalPricing: ModalPricingType
  setModalPricing: React.Dispatch<React.SetStateAction<ModalPricingType>>
  // SC-10999: multi-contas e multi-empresas
  loginUuid?: string
  accounts: SessionAccount[]
  groups: SessionGroupOption[]
  // null fora da visão multi-empresas
  groupSession: GroupSession | null
  refreshAccounts: () => Promise<void>
  switchAccount: (
    target: SwitchAccountTarget,
    options?: SwitchAccountOptions
  ) => Promise<boolean>
  switchingTo: string | null
}

export const AuthContext = createContext({} as AuthContextType)

export function AuthProvider({ children }: Props) {
  const decodeCapabilitites = () => {
    const { ['nextauth.token']: token } = parseCookies()

    if (!token) return null

    const decodedData: any = jwtDecode(token)
    return decodedData.capabilities
  }

  const decodeShowUpgradeModal = () => {
    const { ['nextauth.token']: token } = parseCookies()

    if (!token) return false

    const decodedData: any = jwtDecode(token)
    return !!decodedData.show_upgrade_modal
  }

  const decodeShowNewPlansModal = () => {
    const { ['nextauth.token']: token } = parseCookies()

    if (!token) return false

    const decodedData: any = jwtDecode(token)
    return !!decodedData.show_new_plans_modal
  }

  // Merchants no plano Free (descontinuado) e em trial expirado mantêm acesso
  // de leitura/operação, mas não podem criar recursos novos. A flag vem do
  // token, decidida no backend por MerchantCapability#creation_blocked?.
  const decodeCreationBlocked = () => {
    const { ['nextauth.token']: token } = parseCookies()

    if (!token) return false

    const decodedData: any = jwtDecode(token)
    return !!decodedData.creation_blocked
  }

  const getUser = () => {
    const {
      ['nextauth.token']: token,
      ['nextauth.user']: name,
      ['nextauth.user_logo_url']: logo,
      ['nextauth.automatic_transfer']: automatic_transfer,
      ['nextauth.status']: status,
      ['nextauth.role']: role,
      ['nextauth.pro']: pro,
      ['nextauth.nfse']: nfse,
      ['nextauth.uuid']: uuid,
      ['nextauth.email']: email,
      ['nextauth.merchant_email']: merchant_email,
      ['nextauth.installment_billings']: installment_billings,
      ['nextauth.merchant_user_name']: merchant_user_name,
      ['nextauth.document']: document,
      ['nextauth.phone']: phone,
      ['nextauth.subdomain']: subdomain,
      ['nextauth.current_plan']: current_plan,
      ['nextauth.cora_bank']: cora_bank,
      ['nextauth.cora_enabled']: cora_enabled,
      ['nextauth.custom_invoice_domain']: custom_invoice_domain,
      ['nextauth.custom_portal_domain']: custom_portal_domain,
      ['nextauth.acquisition_source']: acquisition_source,
      ['nextauth.acquisition_medium']: acquisition_medium,
      ['nextauth.acquisition_campaign']: acquisition_campaign
    } = parseCookies()

    if (token && role) {
      const decryptRole = decodeBase64(role)
      const isValidRole = Object.keys(REDIRECT_URL_BY_ROLE).find(
        (k) => k === decryptRole
      )

      if (!isValidRole) {
        destroySession()
        return null
      }

      const user = {
        uuid,
        automatic_transfer,
        logo,
        name,
        status: status as UserStatus,
        pro: pro === 'true',
        role: decryptRole,
        email,
        ...(!!merchant_email && { merchant_email }),
        installment_billings: installment_billings === 'true',
        merchant_user_name,
        document: sessionDocument(document),
        ...(!!phone && { phone }),
        nfse: nfse === 'true',
        subdomain,
        current_plan,
        cora_bank: cora_bank === 'true',
        cora_enabled: cora_enabled === 'true',
        custom_invoice_domain,
        custom_portal_domain,
        acquisition_source,
        acquisition_medium,
        acquisition_campaign
      } as User
      return user
    } else {
      destroySession()
      return null
    }
  }

  const getCapabilities = () => {
    const { ['nextauth.token']: token } = parseCookies()

    if (token) {
      // `?? null` porque o token sem o claim (SC-10878) devolve `undefined`, e
      // `undefined !== null` fazia `capabilitiesFromToken` virar true: o guard
      // do efeito abaixo entendia "o token trouxe os acessos" e nunca buscava
      // na API. O estado ficava indefinido para sempre e a interface negava
      // todo recurso gateado.
      return decodeCapabilitites() ?? null
    } else {
      destroySession()
      return null
    }
  }

  const [user, setUser] = useState<User | null>(getUser)
  const [capabilities, setCapabilities] = useState<Capabilities | null>(
    getCapabilities
  )
  // Guarda se o token trazia o claim. O estado nao serve para isso: ele e
  // zerado no logout e no login por token.
  const capabilitiesFromToken = useRef<boolean>(capabilities != null).current
  const [showUpgradeModal, setShowUpgradeModal] = useState<boolean>(
    decodeShowUpgradeModal
  )
  const [showNewPlansModal, setShowNewPlansModal] = useState<boolean>(
    decodeShowNewPlansModal
  )
  const [creationBlocked, setCreationBlocked] = useState<boolean>(
    decodeCreationBlocked
  )

  // O JWT dura 7 dias, então a flag do token envelhece: quem assina um plano
  // continuaria bloqueado na UI até o próximo login. O DataService emite este
  // evento a cada leitura do perfil, que é a fonte fresca (SC-10363).
  useEffect(() => {
    const handleCreationBlockedSync = (event: Event) => {
      const { creationBlocked: fresh } =
        (event as CustomEvent<CreationBlockedSyncDetail>).detail || {}

      if (typeof fresh === 'boolean') setCreationBlocked(fresh)
    }

    window.addEventListener(
      CREATION_BLOCKED_SYNC_EVENT,
      handleCreationBlockedSync
    )

    return () =>
      window.removeEventListener(
        CREATION_BLOCKED_SYNC_EVENT,
        handleCreationBlockedSync
      )
  }, [])

  // O claim `capabilities` saiu do JWT (SC-10878), então quem chega com token
  // antigo — que ainda o carrega — é atendido pelo claim, e quem chega sem ele
  // busca os acessos na API. Sem esta busca o estado ficaria nulo e o
  // `useModuleAccess` negaria acesso (`?? false`), mostrando modal de upgrade
  // em recurso já contratado.
  //
  // Roda uma vez por montagem, e não a cada mudança de `capabilities`: o
  // logout e o login por token zeram o estado, e depender dele aqui dispararia
  // uma busca no meio desses fluxos.
  useEffect(() => {
    if (capabilitiesFromToken) return

    const { ['nextauth.token']: token } = parseCookies()
    if (!token) return

    let active = true

    const fetchCapabilities = async () => {
      try {
        const response = await DataService({
          type: 'GET',
          url: '/v1/merchant_capabilities'
        })

        const record = response?.data?.record
        if (active && record && typeof record === 'object') {
          setCapabilities(record as Capabilities)
        }
      } catch {
        // Sem os acessos a interface age como plano sem o recurso e oferece
        // upgrade. Errado, mas é o mesmo comportamento de antes desta busca, e
        // a próxima leitura de perfil corrige pelo evento de sincronização.
      }
    }

    fetchCapabilities()

    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // O perfil traz os acessos atuais, então o DataService emite este evento a
  // cada leitura e a interface reflete a troca de plano na mesma sessão.
  useEffect(() => {
    const handleCapabilitiesSync = (event: Event) => {
      const { capabilities: fresh } =
        (event as CustomEvent<CapabilitiesSyncDetail>).detail || {}

      if (fresh && typeof fresh === 'object') {
        setCapabilities(fresh as unknown as Capabilities)
      }
    }

    window.addEventListener(CAPABILITIES_SYNC_EVENT, handleCapabilitiesSync)

    return () =>
      window.removeEventListener(
        CAPABILITIES_SYNC_EVENT,
        handleCapabilitiesSync
      )
  }, [])
  const [modalPricing, setModalPricing] = useState<ModalPricingType>({
    isOpen: false,
    plan: 'basic',
    resourceName: ''
  })

  const [stepCreating, setStepCreating] = useState<string>('')

  // SC-10999: empresas e grupos do login (seletor do topo) e sessão do grupo.
  const [loginUuid, setLoginUuid] = useState<string | undefined>(() =>
    getLoginUuid()
  )
  // Lidos na montagem, como o `getUser`: a guarda de rotas do grupo precisa
  // saber no primeiro render se a sessão é do grupo, antes das telas buscarem
  // dados. O Layout mostra o Loader no primeiro render, então não há diferença
  // de hidratação com o SSR (onde isto é vazio).
  const [accounts, setAccounts] = useState<SessionAccount[]>(
    () => readStoredAccounts(getLoginUuid()).accounts
  )
  const [groups, setGroups] = useState<SessionGroupOption[]>(
    () => readStoredAccounts(getLoginUuid()).groups
  )
  const [groupSession, setGroupSession] = useState<GroupSession | null>(() =>
    readGroupSession()
  )
  const [switchingTo, setSwitchingTo] = useState<string | null>(null)

  const refreshAccounts = useCallback(async () => {
    // Com o token de grupo a rota também devolve as empresas do grupo.
    const response = await DataService({
      type: 'GET',
      url: '/v1/auth/accounts',
      scope: aggregatedScope()
    })

    if (response?.status !== 200 || !response.data) return

    const {
      login_uuid,
      accounts: freshAccounts,
      groups: freshGroups,
      group
    } = response.data as {
      login_uuid?: string
      accounts?: SessionAccount[]
      groups?: SessionGroupOption[]
      group?: SessionGroupPayload | null
    }

    const nextLoginUuid = login_uuid || getLoginUuid()
    if (login_uuid && login_uuid !== getLoginUuid())
      persistLoginUuid(login_uuid)

    saveStoredAccounts(nextLoginUuid, {
      accounts: freshAccounts,
      groups: freshGroups
    })
    setLoginUuid(nextLoginUuid)
    setAccounts(Array.isArray(freshAccounts) ? freshAccounts : [])
    setGroups(Array.isArray(freshGroups) ? freshGroups : [])

    // O `group` desta rota vem sem token: só atualiza a lista de empresas.
    if (group?.merchant_group_uuid && Array.isArray(group.merchants)) {
      saveGroupMerchants(group.merchant_group_uuid, group.merchants)
      setGroupSession(readGroupSession())
    }
  }, [])

  const handleWithLogin = async ({
    response,
    phone,
    token,
    redirectTo
  }: {
    response: any
    token?: string
    phone?: string
    // Troca de conta (SC-10999): volta para a tela em que a pessoa estava.
    redirectTo?: string
  }) => {
    if (response.status === 200) {
      const {
        uuid,
        logo_url,
        name,
        automatic_transfer,
        role,
        status,
        pro,
        email,
        merchant_email,
        document,
        phone: merchantPhone,
        installment_billings,
        merchant_user_name,
        merchant_user_uuid,
        nfse,
        shortcuts,
        portal_personalized,
        show_nps,
        limit_days_for_payment,
        default_days_to_report,
        subdomain,
        days_to_issue_billing,
        issue_billing_type,
        stage,
        free_tier,
        current_plan,
        trial_days_left,
        trial_ends_at,
        cora_bank,
        cora_enabled,
        custom_invoice_domain,
        custom_portal_domain,
        acquisition,
        table_preferences,
        capabilities: capabilitiesFromLogin,
        login_uuid,
        accounts: sessionAccounts,
        groups: sessionGroups,
        group
      } = response.data
      const tok = token || response.data.token

      // SC-10999: seletor de contas e visão multi-empresas. Gravados antes do
      // `nextauth.token` para o `permissions-updated` já encontrar o grupo.
      // Sessão de empresa não herda cookie de grupo de uma sessão anterior.
      if (!group?.token) destroyGroupCookies()
      persistLoginUuid(login_uuid)
      if (login_uuid) {
        saveStoredAccounts(login_uuid, {
          accounts: sessionAccounts,
          groups: sessionGroups
        })
      }
      persistGroupSession(group as SessionGroupPayload | null)
      const isGroupSession = !!(group?.merchant_group_uuid && group?.token)

      const encryptRole = encodeBase64(role)

      setCookie(
        undefined,
        'nextauth.subdomain',
        subdomain,
        SESSION_COOKIE_OPTIONS
      )

      setCookie(undefined, 'nextauth.email', email, SESSION_COOKIE_OPTIONS)

      if (merchant_email) {
        setCookie(
          undefined,
          'nextauth.merchant_email',
          merchant_email,
          SESSION_COOKIE_OPTIONS
        )
      }

      setCookie(
        undefined,
        'nextauth.installment_billings',
        installment_billings,
        SESSION_COOKIE_OPTIONS
      )

      setCookie(
        undefined,
        'nextauth.limit_days_for_payment',
        limit_days_for_payment,
        SESSION_COOKIE_OPTIONS
      )

      setCookie(
        undefined,
        'nextauth.days_to_issue_billing',
        days_to_issue_billing,
        SESSION_COOKIE_OPTIONS
      )

      // SC-11742: prazo padrão de negativação, que preenche o campo "Negativar".
      setCookie(
        undefined,
        'nextauth.default_days_to_report',
        String(default_days_to_report ?? 30),
        SESSION_COOKIE_OPTIONS
      )

      setCookie(
        undefined,
        'nextauth.issue_billing_type',
        issue_billing_type || 'days_before_due_date',
        SESSION_COOKIE_OPTIONS
      )

      setCookie(
        undefined,
        'nextauth.show_nps',
        show_nps,
        SESSION_COOKIE_OPTIONS
      )
      setCookie(undefined, 'nextauth.stage', stage, SESSION_COOKIE_OPTIONS)
      setCookie(undefined, 'nextauth.token', tok, SESSION_COOKIE_OPTIONS)

      window.dispatchEvent(new Event('permissions-updated'))

      setCookie(undefined, 'nextauth.uuid', uuid, SESSION_COOKIE_OPTIONS)

      setCookie(undefined, 'nextauth.status', status, SESSION_COOKIE_OPTIONS)

      !!logo_url &&
        setCookie(
          undefined,
          'nextauth.user_logo_url',
          logo_url,
          SESSION_COOKIE_OPTIONS
        )

      setCookie(undefined, 'nextauth.user', name, SESSION_COOKIE_OPTIONS)
      setCookie(
        undefined,
        'nextauth.current_plan',
        current_plan,
        SESSION_COOKIE_OPTIONS
      )

      // Canal de aquisição (SC-10813): guardado no login para que os eventos de
      // produto possam ser segmentados por origem. Ausente para contas criadas
      // antes do UTM ou sem campanha.
      acquisition?.source &&
        setCookie(
          undefined,
          'nextauth.acquisition_source',
          acquisition.source,
          SESSION_COOKIE_OPTIONS
        )
      acquisition?.medium &&
        setCookie(
          undefined,
          'nextauth.acquisition_medium',
          acquisition.medium,
          SESSION_COOKIE_OPTIONS
        )
      acquisition?.campaign &&
        setCookie(
          undefined,
          'nextauth.acquisition_campaign',
          acquisition.campaign,
          SESSION_COOKIE_OPTIONS
        )
      setCookie(
        undefined,
        'nextauth.merchant_user_name',
        merchant_user_name,
        SESSION_COOKIE_OPTIONS
      )

      setCookie(
        undefined,
        'nextauth.merchant_user_uuid',
        merchant_user_uuid,
        SESSION_COOKIE_OPTIONS
      )

      setCookie(
        undefined,
        'nextauth.shortcuts',
        shortcuts,
        SESSION_COOKIE_OPTIONS
      )

      setCookie(
        undefined,
        'nextauth.automatic_transfer',
        automatic_transfer,
        SESSION_COOKIE_OPTIONS
      )

      setCookie(undefined, 'nextauth.role', encryptRole, SESSION_COOKIE_OPTIONS)

      setCookie(undefined, 'nextauth.pro', pro, SESSION_COOKIE_OPTIONS)

      setCookie(undefined, 'nextauth.nfse', nfse, SESSION_COOKIE_OPTIONS)

      setCookie(
        undefined,
        'nextauth.document',
        sessionDocument(document),
        SESSION_COOKIE_OPTIONS
      )

      if (merchantPhone) {
        setCookie(
          undefined,
          'nextauth.phone',
          merchantPhone,
          SESSION_COOKIE_OPTIONS
        )
      }

      setCookie(
        undefined,
        'nextauth.free_tier',
        free_tier,
        SESSION_COOKIE_OPTIONS
      )

      if (cora_bank !== undefined) {
        setCookie(
          undefined,
          'nextauth.cora_bank',
          String(cora_bank),
          SESSION_COOKIE_OPTIONS
        )
      }

      if (cora_enabled !== undefined) {
        setCookie(
          undefined,
          'nextauth.cora_enabled',
          String(cora_enabled),
          SESSION_COOKIE_OPTIONS
        )
      }

      if (custom_invoice_domain !== undefined) {
        setCookie(
          undefined,
          'nextauth.custom_invoice_domain',
          custom_invoice_domain,
          SESSION_COOKIE_OPTIONS
        )
      }

      if (custom_portal_domain !== undefined) {
        setCookie(
          undefined,
          'nextauth.custom_portal_domain',
          custom_portal_domain,
          SESSION_COOKIE_OPTIONS
        )
      }

      setUser({
        automatic_transfer,
        name,
        role,
        pro,
        status,
        installment_billings,
        uuid,
        ...(!!logo_url && { logo: logo_url }),
        email,
        merchant_user_name,
        document: sessionDocument(document),
        ...(!!merchantPhone && { phone: merchantPhone }),
        nfse,
        portal_personalized,
        stage,
        current_plan,
        trial_days_left,
        trial_ends_at,
        subdomain,
        cora_bank,
        cora_enabled,
        custom_invoice_domain,
        custom_portal_domain,
        // Espelha o que o getUser() já lê dos cookies (SC-10944). Sem isto o
        // `user` em memória logo após o login fica sem canal, o
        // registerSuperProperties filtra os undefined e o `first_login` — que
        // dispara uma única vez por conta — sai para sempre sem origem.
        ...(!!acquisition?.source && {
          acquisition_source: acquisition.source
        }),
        ...(!!acquisition?.medium && {
          acquisition_medium: acquisition.medium
        }),
        ...(!!acquisition?.campaign && {
          acquisition_campaign: acquisition.campaign
        })
      })
      // O login traz os acessos no proprio payload (SC-10878). O
      // `decodeCapabilitites` continua como retaguarda para o token antigo,
      // que ainda carrega o claim, enquanto a versao anterior estiver no ar.
      setCapabilities(capabilitiesFromLogin ?? decodeCapabilitites())
      setShowUpgradeModal(decodeShowUpgradeModal())
      setShowNewPlansModal(decodeShowNewPlansModal())
      setCreationBlocked(decodeCreationBlocked())
      // Hard reload em vez de Router.push pelo mesmo motivo do signOut:
      // o widget SendPulse fica montado no _document e ja gerou um
      // spSubscriberId novo na tela de login (sem token). Sem reload,
      // restoreSubscriberAndLoad() nao roda de novo e o widget mantem
      // o subscriber gerado em vez de puxar o do usuario logado do
      // backend. Resultado: historico so aparece depois de Ctrl+R.
      const roleTarget =
        getRedirectUrlByRole()[
          role as keyof ReturnType<typeof getRedirectUrlByRole>
        ]
      // Na visão do grupo só algumas telas funcionam: a inicial é a Visão
      // Geral, e a tela de origem da troca só é mantida se valer no grupo.
      const target = phone
        ? `/visao-geral?phone=${phone}`
        : redirectTo && (!isGroupSession || isPathAllowedInGroup(redirectTo))
        ? redirectTo
        : isGroupSession
        ? '/visao-geral'
        : roleTarget
      if (typeof window !== 'undefined') {
        // Backend e a fonte da verdade: hidrata o cache local de preferencias
        // de tabela antes do hard reload. Na proxima montagem o useTableColumns
        // le o cache ja populado (sem flash). Se o payload nao trouxer o campo
        // (backend antigo), hydratePrefs apenas limpa o cache e segue.
        hydratePrefs(merchant_user_uuid, table_preferences)
        window.location.assign(target)
      } else {
        Router.push(target)
      }
    }
  }

  // Tela para onde a troca de conta volta. Tela de um registro (rota dinâmica,
  // como /clientes/[id]) e parâmetros que apontam para um registro são da
  // empresa anterior: nesses casos vale a tela inicial da nova sessão.
  const getSwitchRedirect = () => {
    if (typeof window === 'undefined' || Router.pathname.includes('[')) {
      return undefined
    }

    const params = new URLSearchParams(window.location.search)
    Array.from(params.keys()).forEach((param) => {
      if (
        param.endsWith('_uuid') ||
        param === 'merchant_uuids' ||
        param === 'id'
      ) {
        params.delete(param)
      }
    })

    const search = params.toString()
    return `${window.location.pathname}${search ? `?${search}` : ''}`
  }

  // SC-10999: troca de empresa (ou entrada/saída da visão multi-empresas) sem
  // novo login. A resposta tem o formato do sign_in e é gravada como um login,
  // depois de descartar a sessão atual por inteiro: o handleWithLogin só grava
  // alguns cookies quando vêm preenchidos, e o valor da empresa anterior
  // sobraria.
  const switchAccount = async (
    target: SwitchAccountTarget,
    options?: SwitchAccountOptions
  ) => {
    const key = target.merchantGroupUuid || target.merchantUuid
    if (!key || switchingTo) return false

    setSwitchingTo(key)

    const response = await DataService({
      url: '/v1/auth/switch',
      data: target.merchantGroupUuid
        ? { merchant_group_uuid: target.merchantGroupUuid }
        : { merchant_uuid: target.merchantUuid },
      // Na visão do grupo vai o token de grupo, que é o revogado pela troca.
      ...(getGroupToken() && { scope: 'group' as const })
    })

    if (response?.status !== 200 || !response?.data?.token) {
      setSwitchingTo(null)
      toast.error(
        response?.message ||
          'Não foi possível trocar de conta. Tente novamente.'
      )
      return false
    }

    const redirectTo = options?.redirectTo ?? getSwitchRedirect()

    destroySession()
    clearMerchantTokens()
    if (typeof window !== 'undefined') {
      MERCHANT_SCOPED_STORAGE_KEYS.forEach((storageKey) => {
        try {
          localStorage.removeItem(storageKey)
        } catch {
          //
        }
      })

      try {
        mixpanel.reset()
      } catch {
        //
      }
    }

    await handleWithLogin({ response, redirectTo })
    return true
  }

  async function signIn({
    email,
    password,
    login_type = 'email',
    id_token,
    phone,
    cf_turnstile_token
  }: SignInParams) {
    setUser(null)
    setCapabilities(null)
    setShowUpgradeModal(false)
    setShowNewPlansModal(false)
    setCreationBlocked(false)
    const data = {
      email: email.trim(),
      login_type,
      ...(!!password && { password }),
      ...(!!id_token && { id_token }),
      ...(!!cf_turnstile_token && { cf_turnstile_token })
    }

    setCookie(undefined, 'nextauth.email', email, SESSION_COOKIE_OPTIONS)

    const response = await DataService({ data, url: '/v2/sign_in' })
    await handleWithLogin({ response, phone })
    return response as SignInResponse
  }

  async function signInWithToken({ token }: { token: string }) {
    setUser(null)
    setCapabilities(null)
    setShowUpgradeModal(false)
    setShowNewPlansModal(false)
    setCreationBlocked(false)
    //destroySession()
    const response = await axios.get(`${baseURL}/v1/merchants/preferences`, {
      headers: { Authorization: `Bearer ${token}` }
    })

    await handleWithLogin({ response, token })
  }
  const { data: sessionData } = useSession()
  const sessionDatatyped = sessionData as ExtendedSession
  const signOut = async () => {
    try {
      if (sessionDatatyped?.user) {
        await handleGoogleSignOut({
          accessToken: sessionDatatyped?.accessToken as string
        })
      }
    } catch (error) {
      console.error('Erro ao fazer sign out do Google:', error)
    }

    // Remove the web push subscription from this device before auth ends,
    // so the next user of this browser does not inherit pushes.
    try {
      if (
        typeof window !== 'undefined' &&
        'serviceWorker' in navigator &&
        'PushManager' in window
      ) {
        const registration = await navigator.serviceWorker.ready
        const subscription = await registration.pushManager.getSubscription()
        if (subscription) {
          const endpoint = subscription.endpoint
          await subscription.unsubscribe()
          await DataService({
            type: 'DELETE',
            url: '/v1/push/subscribe',
            data: { endpoint }
          })
        }
      }
    } catch (error) {
      console.error('Erro ao remover push subscription:', error)
    }

    // SC-10999: na visão do grupo o token de grupo também é revogado.
    const groupToken = getGroupToken()
    const currentLoginUuid = getLoginUuid()
    const currentGroupUuid = readGroupSession()?.merchant_group_uuid

    // Os tokens de empresa pedidos na visão do grupo valem 7 dias no servidor:
    // revogados junto, em vez de ficarem válidos depois do logout.
    const merchantTokens = listMerchantTokens()
    await DataService({
      type: 'PATCH',
      url: '/v2/auth/logout',
      ...((!!groupToken || merchantTokens.length > 0) && {
        data: {
          ...(!!groupToken && { group_token: groupToken }),
          ...(merchantTokens.length > 0 && { tokens: merchantTokens })
        }
      })
    })
    destroySession()
    clearStoredSession(currentLoginUuid, currentGroupUuid)
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem('chat-assistant-history')
      } catch (e) {
        //
      }

      // SC-10692: sem o reset, o distinct_id do usuario que saiu continua no
      // storage do mixpanel e o proximo identify tenta reaproveitar um id
      // anonimo ja atribuido. Em prod isso gerava 5.818 eventos/30d com
      // $identity_failure_reason = errAnonDistinctIdAssignedAlready, ou seja,
      // eventos de uma conta colando no perfil de outra em maquina
      // compartilhada. O hard reload abaixo nao resolve: o id vive em cookie.
      try {
        mixpanel.reset()
      } catch (e) {
        //
      }
    }

    setUser(null)
    setCapabilities(null)
    setShowUpgradeModal(false)
    setShowNewPlansModal(false)
    setCreationBlocked(false)
    // Hard reload em vez de Router.push: o widget SendPulse fica montado
    // no _document e mantem o spSubscriberId em memoria mesmo depois de
    // limparmos o localStorage. Sem destruir a pagina, o widget regrava
    // o subscriber antigo e o proximo login (outra conta) herda o
    // historico do usuario anterior.
    if (typeof window !== 'undefined') {
      window.location.assign('/login')
    } else {
      Router.push('/login')
    }
  }

  const value = useMemo(
    () => ({
      isAuthenticated: !!user,
      signIn,
      signOut,
      user,
      capabilities,
      showUpgradeModal,
      showNewPlansModal,
      creationBlocked,
      signInWithToken,
      setUser,
      setStepCreating,
      stepCreating,
      modalPricing,
      setModalPricing,
      loginUuid,
      accounts,
      groups,
      groupSession,
      refreshAccounts,
      switchAccount,
      switchingTo
    }),
    [
      user,
      signIn,
      signOut,
      user,
      capabilities,
      showUpgradeModal,
      showNewPlansModal,
      creationBlocked,
      signInWithToken,
      setUser,
      modalPricing,
      setModalPricing,
      loginUuid,
      accounts,
      groups,
      groupSession,
      refreshAccounts,
      switchAccount,
      switchingTo
    ]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuthContext should be used within an AuthContext')
  }

  return context as AuthContextType
}
